// Shape pictures (14 Oct 2026). A `shape_2d` is one named flat shape, with
// optional labels on its sides and in its corners and optional marks for
// equal sides, right angles and parallel sides. A `composite_shape` is an
// outline whose sides all meet at right angles, given by its corner points.
//
// A named shape is not drawn to scale, but it never contradicts its labels
// (2 Nov 2026): where the labels give lengths or angle sizes, the outline
// follows them within limits that keep it readable (a side labelled longer
// is drawn longer, a corner labelled 116° is drawn wide), and a triangle
// that cannot exist is still drawn with its sides in the right order. With
// no numbers, every isosceles triangle is the same drawing. A composite
// shape is drawn to scale from its points. A side marked "?" carries no
// length anywhere: it is drawn, and never written or spoken.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

const SHAPE_NAMES = [
  'circle',
  'semicircle',
  // Year 1 (24 Oct 2026): one curved line, no corners, wider than tall.
  'oval',
  'equilateral_triangle',
  'isosceles_triangle',
  'scalene_triangle',
  'right_triangle',
  'square',
  'rectangle',
  'rhombus',
  'parallelogram',
  'trapezium',
  'kite',
  'pentagon',
  'hexagon',
  'heptagon',
  'octagon',
  'nonagon',
  'decagon',
  'star_5',
  'plus_sign',
  'heart',
  'l_shape',
  'arrow',
] as const;

export type ShapeName = (typeof SHAPE_NAMES)[number];

export type ShapeMarks = {
  equalSides: boolean;
  rightAngles: boolean;
  parallel: boolean;
};

// (Year 8, 2 Nov 2026) Where a right triangle has its right angle. Its
// bottom side always lies flat: with the right angle at the top, the
// longest side is the flat one at the bottom.
export const RIGHT_ANGLE_CORNERS = [
  'bottom_left',
  'bottom_right',
  'top',
] as const;
export type RightAngleCorner = (typeof RIGHT_ANGLE_CORNERS)[number];

export type Shape2dSpec = {
  type: 'shape_2d';
  shape: ShapeName;
  // One label for each side, clockwise from the bottom side (a kite: from
  // the lower left side). A length ("8 cm"), a letter, "?" for the side to
  // find, or "" for a bare side. Empty when the shape takes no labels.
  // (Year 8, 2 Nov 2026) A side may also carry a short expression in one
  // letter, such as "3x", "x + 4", "2x − 1" or "2(x + 3)".
  sides: string[];
  // One label for each corner, clockwise from the bottom left corner.
  angles: string[];
  marks: ShapeMarks;
  // (2 Nov 2026) The perpendicular height from the bottom side, drawn as a
  // dashed line from the top corner straight down to the bottom side (or to
  // the bottom side carried on, dashed, past its end), with a small square
  // where it meets it. A length, a letter or "?". Triangles (not a right
  // triangle, whose upright side is its height), parallelograms,
  // rhombuses and trapeziums only.
  height?: string;
  // (Year 8, 2 Nov 2026) A right triangle only: the corner with the right
  // angle. Left out, it is at the bottom left, as it always was.
  rightAngleAt?: RightAngleCorner;
};

// The shapes that can carry a perpendicular height.
const HEIGHTS = new Set<ShapeName>([
  'isosceles_triangle',
  'scalene_triangle',
  'parallelogram',
  'rhombus',
  'trapezium',
]);

export type CompositeShapeSpec = {
  type: 'composite_shape';
  // The corners in order. y goes up the page.
  points: { x: number; y: number }[];
  // One label for each side; side 0 runs from the first point to the second.
  sides: string[];
};

// How many sides can carry a label. A shape with curves, or with too many
// short sides to label, has none.
const LABELLED_SIDES: Record<ShapeName, number> = {
  circle: 0,
  semicircle: 0,
  oval: 0,
  equilateral_triangle: 3,
  isosceles_triangle: 3,
  scalene_triangle: 3,
  right_triangle: 3,
  square: 4,
  rectangle: 4,
  rhombus: 4,
  parallelogram: 4,
  trapezium: 4,
  kite: 4,
  pentagon: 5,
  hexagon: 6,
  heptagon: 7,
  octagon: 8,
  nonagon: 9,
  decagon: 10,
  star_5: 0,
  plus_sign: 0,
  heart: 0,
  l_shape: 6,
  arrow: 0,
};

const REGULAR: Partial<Record<ShapeName, number>> = {
  pentagon: 5,
  hexagon: 6,
  heptagon: 7,
  octagon: 8,
  nonagon: 9,
  decagon: 10,
};

// More corners than this cannot be told apart on a phone.
const MAX_COMPOSITE_POINTS = 20;

const LENGTH = /^\d+(?:\.\d+)?(?: ?(?:mm|cm|m|km))?$/;
const LETTER = /^[a-zA-Z]$/;
const DEGREES = /^(\d{1,3}(?:\.\d+)?)°$/;

function isSideLabel(text: string): boolean {
  return text === '' || text === '?' || LETTER.test(text) || LENGTH.test(text);
}

// The longest expression a side can carry: it still fits beside a short
// side on a phone.
const EXPRESSION_LONGEST = 12;
const MINUS = '−';
const TIMES = '×';
const ONE_LETTER = /^[a-zA-Z]$/;

// (Year 8, 2 Nov 2026) A short expression in one letter: numbers, the
// letter, +, a true minus sign, × and brackets, such as "3x", "x + 4",
// "2x − 1" or "2(x + 3)". A plain letter or length is not one. The hyphen
// is refused, so that every minus is read and drawn the same way.
export function isExpressionLabel(text: string): boolean {
  if (text.length > EXPRESSION_LONGEST || LETTER.test(text)) return false;
  const tokens = text.match(/\d+(?:\.\d+)?|[a-zA-Z]|[+()−×]|\s+/g);
  if (tokens?.join('') !== text) return false;
  const parts = tokens.filter((token) => token.trim() !== '');
  const letters = new Set(parts.filter((token) => ONE_LETTER.test(token)));
  if (letters.size !== 1) return false;
  // An expression is a term, then + or − and a term, any number of times.
  // A term is an optional minus, then factors side by side or joined by ×.
  // A factor is a number, the letter or a bracket. Only a number or a
  // closing bracket may stand straight before a letter or a bracket, so
  // "xx", "x2" and "x(x + 1)" are refused.
  let at = 0;
  const next = () => parts[at];
  const factor = (): boolean => {
    const token = next();
    if (token === undefined) return false;
    if (token === '(') {
      at += 1;
      if (!expression() || next() !== ')') return false;
      at += 1;
      return true;
    }
    if (/^\d/.test(token) || ONE_LETTER.test(token)) {
      at += 1;
      return true;
    }
    return false;
  };
  const term = (): boolean => {
    if (next() === MINUS) at += 1;
    if (!factor()) return false;
    for (;;) {
      const before = parts[at - 1] ?? '';
      const token = next();
      if (token === TIMES) {
        at += 1;
        if (!factor()) return false;
      } else if (
        token !== undefined &&
        (token === '(' || ONE_LETTER.test(token)) &&
        /^[\d)]/.test(before)
      ) {
        if (!factor()) return false;
      } else return true;
    }
  };
  const expression = (): boolean => {
    if (!term()) return false;
    while (next() === '+' || next() === MINUS) {
      at += 1;
      if (!term()) return false;
    }
    return true;
  };
  return expression() && at === parts.length;
}

// A side label on a named shape: as on any shape, or an expression.
function isShape2dSideLabel(text: string): boolean {
  return isSideLabel(text) || isExpressionLabel(text);
}

function degreesOf(text: string): number | null {
  const size = DEGREES.exec(text);
  return size ? Number(size[1]) : null;
}

function isAngleLabel(text: string): boolean {
  if (text === '' || text === '?' || LETTER.test(text)) return true;
  const degrees = degreesOf(text);
  return degrees !== null && degrees > 0 && degrees < 180;
}

function readLabels(
  value: unknown,
  count: number,
  allowed: (text: string) => boolean,
): string[] | null {
  const bare = Array<string>(count).fill('');
  if (value === undefined) return bare;
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return bare;
  if (value.length !== count) return null;
  const labels: string[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'string') return null;
    const text = entry.trim();
    if (!allowed(text)) return null;
    labels.push(text);
  }
  return labels;
}

function readMarks(value: unknown): ShapeMarks | null {
  const marks = { equalSides: false, rightAngles: false, parallel: false };
  if (value === undefined) return marks;
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null;
  const record = value as Record<string, unknown>;
  for (const name of ['equalSides', 'rightAngles', 'parallel'] as const) {
    const mark = record[name];
    if (mark === undefined) continue;
    if (typeof mark !== 'boolean') return null;
    marks[name] = mark;
  }
  return marks;
}

export function readShape2d(
  record: Record<string, unknown>,
): Shape2dSpec | null {
  const shape = SHAPE_NAMES.find((name) => name === record.shape);
  if (!shape) return null;
  const count = LABELLED_SIDES[shape];
  const sides = readLabels(record.sides, count, isShape2dSideLabel);
  // The corners of an L are not all the same way round, so they take none.
  const angles = readLabels(
    record.angles,
    shape === 'l_shape' ? 0 : count,
    isAngleLabel,
  );
  const marks = readMarks(record.marks);
  if (!sides || !angles || !marks) return null;
  let turned: { rightAngleAt?: RightAngleCorner } = {};
  if (record.rightAngleAt !== undefined) {
    const corner = RIGHT_ANGLE_CORNERS.find(
      (name) => name === record.rightAngleAt,
    );
    if (!corner || shape !== 'right_triangle') return null;
    turned = { rightAngleAt: corner };
  }
  if (record.height === undefined)
    return { type: 'shape_2d', shape, sides, angles, marks, ...turned };
  if (typeof record.height !== 'string' || !HEIGHTS.has(shape)) return null;
  const height = record.height.trim();
  if (height === '' || !isSideLabel(height)) return null;
  return { type: 'shape_2d', shape, sides, angles, marks, height };
}

type Line = { from: ShapePoint; to: ShapePoint };

// True when two sides that are not neighbours touch or cross.
function touches(a: Line, b: Line): boolean {
  const turn = (p: ShapePoint, q: ShapePoint, r: ShapePoint) =>
    Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
  const within = (p: ShapePoint, q: ShapePoint, r: ShapePoint) =>
    Math.min(p.x, q.x) <= r.x &&
    r.x <= Math.max(p.x, q.x) &&
    Math.min(p.y, q.y) <= r.y &&
    r.y <= Math.max(p.y, q.y);
  const d1 = turn(b.from, b.to, a.from);
  const d2 = turn(b.from, b.to, a.to);
  const d3 = turn(a.from, a.to, b.from);
  const d4 = turn(a.from, a.to, b.to);
  if (d1 !== d2 && d3 !== d4) return true;
  return (
    (d1 === 0 && within(b.from, b.to, a.from)) ||
    (d2 === 0 && within(b.from, b.to, a.to)) ||
    (d3 === 0 && within(a.from, a.to, b.from)) ||
    (d4 === 0 && within(a.from, a.to, b.to))
  );
}

function sidesOf(corners: ShapePoint[]): Line[] {
  return corners.map((corner, index) => ({
    from: corner,
    to: corners[(index + 1) % corners.length] ?? corner,
  }));
}

export function readCompositeShape(
  record: Record<string, unknown>,
): CompositeShapeSpec | null {
  const { points, labels } = record;
  if (
    !Array.isArray(points) ||
    points.length < 4 ||
    points.length > MAX_COMPOSITE_POINTS ||
    points.length % 2 !== 0
  )
    return null;
  const corners: ShapePoint[] = [];
  for (const entry of points as unknown[]) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const [x, y] = entry as unknown[];
    if (typeof x !== 'number' || typeof y !== 'number') return null;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    corners.push({ x, y });
  }
  // Right angles only: the sides go across and up or down in turn.
  const lines = sidesOf(corners);
  for (const [index, line] of lines.entries()) {
    const across = line.from.y === line.to.y && line.from.x !== line.to.x;
    const upright = line.from.x === line.to.x && line.from.y !== line.to.y;
    if (!across && !upright) return null;
    const before = lines[(index + lines.length - 1) % lines.length];
    if (before && (before.from.y === before.to.y) === across) return null;
  }
  // The outline never touches or crosses itself.
  for (const [index, line] of lines.entries())
    for (let other = index + 2; other < lines.length; other += 1) {
      if (index === 0 && other === lines.length - 1) continue;
      const far = lines[other];
      if (far && touches(line, far)) return null;
    }
  const sides = Array<string>(corners.length).fill('');
  if (labels !== undefined) {
    if (!Array.isArray(labels)) return null;
    const seen = new Set<number>();
    for (const entry of labels as unknown[]) {
      if (typeof entry !== 'object' || entry === null) return null;
      const { side, text } = entry as Record<string, unknown>;
      if (typeof side !== 'number' || !Number.isInteger(side)) return null;
      if (side < 0 || side >= corners.length || seen.has(side)) return null;
      if (typeof text !== 'string') return null;
      const label = text.trim();
      if (label === '' || !isSideLabel(label)) return null;
      seen.add(side);
      sides[side] = label;
    }
  }
  return { type: 'composite_shape', points: corners, sides };
}

// The two sloping sides of a trapezium are drawn equal only when their
// labels say they are.
function isIsoscelesTrapezium(shape: Shape2dSpec): boolean {
  const [, left, , right] = shape.sides;
  return left !== undefined && left !== '' && left !== '?' && left === right;
}

// Which sides carry matching marks for equal sides. The first group has one
// mark on each side, the second group two.
function equalGroups(shape: Shape2dSpec): number[][] {
  if (!shape.marks.equalSides) return [];
  const regular = REGULAR[shape.shape];
  if (regular !== undefined)
    return [Array.from({ length: regular }, (_, index) => index)];
  switch (shape.shape) {
    case 'equilateral_triangle':
      return [[0, 1, 2]];
    case 'isosceles_triangle':
      return [[1, 2]];
    case 'square':
    case 'rhombus':
      return [[0, 1, 2, 3]];
    case 'rectangle':
    case 'parallelogram':
      return [
        [0, 2],
        [1, 3],
      ];
    case 'kite':
      return [
        [0, 3],
        [1, 2],
      ];
    case 'trapezium':
      return isIsoscelesTrapezium(shape) ? [[1, 3]] : [];
    default:
      return [];
  }
}

// Which pairs of sides carry matching arrows for parallel sides.
function parallelGroups(shape: Shape2dSpec): number[][] {
  if (!shape.marks.parallel) return [];
  switch (shape.shape) {
    case 'square':
    case 'rectangle':
    case 'rhombus':
    case 'parallelogram':
      return [
        [0, 2],
        [1, 3],
      ];
    case 'trapezium':
      return [[0, 2]];
    default:
      return [];
  }
}

// An expression read aloud: "x + 4" is "x plus 4", "2x − 1" is "2x minus
// 1", "2(x + 3)" is "2 open bracket x plus 3 close bracket".
export function spokenExpression(text: string): string {
  const words: Record<string, string> = {
    '+': 'plus',
    [MINUS]: 'minus',
    [TIMES]: 'times',
    '(': 'open bracket',
    ')': 'close bracket',
  };
  return (text.match(/\d+(?:\.\d+)?[a-zA-Z]?|[a-zA-Z]|[+()−×]/g) ?? [])
    .map((token) => words[token] ?? token)
    .join(' ');
}

function spokenSide(text: string): string {
  if (text === '?') return 'an unknown side marked with a question mark';
  if (isExpressionLabel(text)) return spokenExpression(text);
  return LETTER.test(text) ? `the letter ${text}` : text;
}

function spokenAngle(text: string): string {
  if (text === '?') return 'an unknown angle marked with a question mark';
  if (LETTER.test(text)) return `the letter ${text}`;
  const degrees = degreesOf(text) ?? 0;
  return degrees === 1 ? '1 degree' : `${String(degrees)} degrees`;
}

// The labels in order. Neighbours with no label are counted together.
function spokenList(
  labels: string[],
  spoken: (text: string) => string,
  thing: string,
): string {
  const parts: string[] = [];
  let bare = 0;
  const flush = () => {
    if (bare === 1) parts.push(`a ${thing} with no label`);
    if (bare > 1) parts.push(`${String(bare)} ${thing}s with no label`);
    bare = 0;
  };
  for (const label of labels) {
    if (label === '') {
      bare += 1;
      continue;
    }
    flush();
    parts.push(spoken(label));
  }
  flush();
  return parts.join(', ');
}

// The outline in plain words. A shape with straight sides is never named
// (triangle and the count of sides only): its name is often what the child
// is working toward, and its labels and marks are given after it.
function outlineWords(shape: ShapeName): string {
  switch (shape) {
    case 'circle':
      return 'A circle.';
    case 'semicircle':
      return 'Half a circle, with its straight side at the bottom.';
    case 'oval':
      return 'An oval.';
    case 'equilateral_triangle':
    case 'isosceles_triangle':
    case 'scalene_triangle':
    case 'right_triangle':
      return 'A triangle.';
    case 'star_5':
      return 'A star with 5 points.';
    case 'plus_sign':
      return 'A plus sign.';
    case 'heart':
      return 'A heart shape.';
    case 'l_shape':
      return 'A shape with 6 straight sides, like a capital letter L.';
    case 'arrow':
      return 'An arrow pointing to the right.';
    default:
      return `A shape with ${String(LABELLED_SIDES[shape])} straight sides.`;
  }
}

function equalWords(shape: Shape2dSpec): string | null {
  if (equalGroups(shape).length === 0) return null;
  switch (shape.shape) {
    case 'isosceles_triangle':
    case 'trapezium':
      return 'Small marks show that the two sloping sides are the same length.';
    case 'rectangle':
    case 'parallelogram':
      return 'Matching marks show that opposite sides are the same length.';
    case 'kite':
      return 'Matching marks show that the two lower sides are the same length, and that the two upper sides are the same length.';
    default:
      return `Small marks show that all ${String(LABELLED_SIDES[shape.shape])} sides are the same length.`;
  }
}

const RIGHT_ANGLE_WORDS: Record<RightAngleCorner, string> = {
  bottom_left: 'bottom left',
  bottom_right: 'bottom right',
  top: 'top',
};

function rightAngleWords(shape: Shape2dSpec): string | null {
  const corners = cornersOf(shape);
  const squares = corners ? rightCorners(shape, corners).length : 0;
  if (!corners || squares === 0) return null;
  if (squares === corners.length)
    return 'Every corner is marked as a right angle.';
  if (shape.shape === 'right_triangle')
    return `The ${RIGHT_ANGLE_WORDS[shape.rightAngleAt ?? 'bottom_left']} corner is marked as a right angle.`;
  return squares === 1
    ? '1 corner is marked as a right angle.'
    : `${String(squares)} corners are marked as right angles.`;
}

function parallelWords(shape: Shape2dSpec): string | null {
  const groups = parallelGroups(shape).length;
  if (groups === 0) return null;
  return groups === 1
    ? 'Arrows show that the top and bottom sides are parallel.'
    : 'Matching arrows show that opposite sides are parallel.';
}

// What a screen reader says. It gives the labels and marks that are drawn
// and nothing else: never the name of a shape with straight sides, and
// never a length or an angle that is not written on the picture.
export function describeShape2d(shape: Shape2dSpec): string {
  const kite = shape.shape === 'kite';
  const parts = [outlineWords(shape.shape)];
  const [first] = shape.sides;
  // One label on every side is said once.
  if (first && shape.sides.every((label) => label === first))
    parts.push(
      first === '?'
        ? `Each of its ${String(shape.sides.length)} sides is unknown and is marked with a question mark.`
        : `Each of its ${String(shape.sides.length)} sides is labelled ${spokenSide(first)}.`,
    );
  else if (shape.sides.some((label) => label !== ''))
    parts.push(
      `Its sides, going clockwise from the ${kite ? 'lower left' : 'bottom'} side, are labelled: ${spokenList(shape.sides, spokenSide, 'side')}.`,
    );
  if (shape.angles.some((label) => label !== ''))
    parts.push(
      `Its corners, going clockwise from the ${kite ? 'left' : 'bottom left'} corner, are labelled: ${spokenList(shape.angles, spokenAngle, 'corner')}.`,
    );
  if (shape.height)
    parts.push(
      `A dashed line from the top corner straight down to the bottom side shows the perpendicular height. ${shape.height === '?' ? 'It is unknown and is marked with a question mark.' : `It is labelled ${spokenSide(shape.height)}.`}`,
    );
  for (const words of [
    equalWords(shape),
    rightAngleWords(shape),
    parallelWords(shape),
  ])
    if (words !== null) parts.push(words);
  return parts.join(' ');
}

export function describeCompositeShape(shape: CompositeShapeSpec): string {
  const count = shape.points.length;
  // Every corner turns the same way round the shape except the ones that
  // point inwards. A shape with n corners has (n - 4) / 2 of those.
  const inward = (count - 4) / 2;
  const parts = [
    `A shape with ${String(count)} straight sides that meet at square corners.`,
  ];
  if (inward === 1) parts.push('1 of its corners points inwards.');
  if (inward > 1) parts.push(`${String(inward)} of its corners point inwards.`);
  parts.push(
    shape.sides.some((label) => label !== '')
      ? `Going round the shape, its sides are labelled: ${spokenList(shape.sides, spokenSide, 'side')}.`
      : 'Its sides have no labels.',
  );
  return parts.join(' ');
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type ShapePoint = { x: number; y: number };
// A label's box, centred on (x, y).
export type ShapePictureLabel = ShapePoint & {
  text: string;
  width: number;
  height: number;
};
export type ShapePicture = {
  // The outline, as the `d` of an SVG path.
  outline: string;
  // The corners, clockwise from the right-hand end of the bottom side. A
  // shape with a curved side has none.
  corners: ShapePoint[];
  // Each label sits outside the side it belongs to.
  sideLabels: ShapePictureLabel[];
  // Each label sits inside its corner.
  angleLabels: ShapePictureLabel[];
  // Short strokes across the sides that are equal.
  ticks: Line[];
  // Arrow heads on the sides that are parallel.
  arrows: ShapePoint[][];
  // A small square in each corner marked as a right angle.
  squares: ShapePoint[][];
  // Dashed lines: a perpendicular height, and the bottom side carried on
  // to meet it when it falls outside the shape.
  dashes: Line[];
  // The label of the perpendicular height, beside its dashed line.
  heightLabels: ShapePictureLabel[];
  box: { x: number; y: number; width: number; height: number };
};

export const SHAPE_LABEL_SIZE = 14;
const LABEL_HEIGHT = 15;
const PAD = 6;
// Between a side and its label: room for a mark across the side.
const GAP = 9;
const TICK = 5;
const ARROW = 7;
const SQUARE = 11;
// A composite shape is drawn to fit inside this.
const COMPOSITE_WIDTH = 200;
const COMPOSITE_HEIGHT = 150;
// The least gap between two labels in a corner that points inwards, so
// that each is seen to belong to its own side.
const APART = 7;
// How much larger it is drawn when its labels have no room at any size.
const GROWTH = 2;
// Where along its side a label is tried, the middle first.
const PLACES = [0.5, 0.4, 0.6, 0.3, 0.7, 0.22, 0.78];
// The widest a drawing that follows its labels is made, labels and all,
// unless the usual drawing of its shape is wider: shown in a phone-wide
// answer card, a picture this wide keeps its labels at their full size.
const WIDEST = 172;

const fixed = (value: number) => value.toFixed(2);

// Roughly how wide a label is drawn, to keep it off the lines.
function labelWidth(text: string): number {
  const signs = text.split('°').length - 1;
  return (text.length - signs) * 8.6 + signs * 6;
}

function unit(from: ShapePoint, to: ShapePoint): ShapePoint {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  return { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
}

function along(from: ShapePoint, way: ShapePoint, reach: number): ShapePoint {
  return { x: from.x + way.x * reach, y: from.y + way.y * reach };
}

function between(line: Line, part: number): ShapePoint {
  return {
    x: line.from.x + (line.to.x - line.from.x) * part,
    y: line.from.y + (line.to.y - line.from.y) * part,
  };
}

// True when the corners go clockwise as they are seen on the screen.
function isClockwise(corners: ShapePoint[]): boolean {
  let twice = 0;
  for (const line of sidesOf(corners))
    twice += line.from.x * line.to.y - line.to.x * line.from.y;
  return twice > 0;
}

// The way out of the shape from one of its sides.
function outwardOf(line: Line, clockwise: boolean): ShapePoint {
  const way = unit(line.from, line.to);
  return clockwise ? { x: way.y, y: -way.x } : { x: -way.y, y: way.x };
}

// True when the line passes through the label's box.
function crosses(box: ShapePictureLabel, line: Line, margin: number): boolean {
  const halfWidth = box.width / 2 + margin;
  const halfHeight = box.height / 2 + margin;
  const dx = line.to.x - line.from.x;
  const dy = line.to.y - line.from.y;
  let enter = 0;
  let leave = 1;
  for (const [step, room] of [
    [-dx, line.from.x - (box.x - halfWidth)],
    [dx, box.x + halfWidth - line.from.x],
    [-dy, line.from.y - (box.y - halfHeight)],
    [dy, box.y + halfHeight - line.from.y],
  ] as const) {
    if (step === 0) {
      if (room < 0) return false;
    } else if (step < 0) enter = Math.max(enter, room / step);
    else leave = Math.min(leave, room / step);
  }
  return enter <= leave;
}

// True when two labels are closer than `apart`.
function overlaps(
  a: ShapePictureLabel,
  b: ShapePictureLabel,
  apart: number,
): boolean {
  return (
    Math.abs(a.x - b.x) < (a.width + b.width) / 2 + apart &&
    Math.abs(a.y - b.y) < (a.height + b.height) / 2 + apart
  );
}

// A label outside each side that has one. It sits beside the middle of its
// side, or further along where the middle has no room, at least `apart`
// from every other label. When `strict`, null is returned if a label has no
// room anywhere; otherwise it takes the middle.
function placeSideLabels(
  corners: ShapePoint[],
  labels: string[],
  inside: ShapePictureLabel[],
  marks: Line[],
  apart: number,
  strict: boolean,
): ShapePictureLabel[] | null {
  const lines = sidesOf(corners);
  const clockwise = isClockwise(corners);
  const placed: ShapePictureLabel[] = [];
  for (const [index, line] of lines.entries()) {
    const text = labels[index] ?? '';
    if (text === '') continue;
    const outward = outwardOf(line, clockwise);
    const width = labelWidth(text);
    const reach =
      (Math.abs(outward.x) * width + Math.abs(outward.y) * LABEL_HEIGHT) / 2;
    const spots = PLACES.map((part) => ({
      ...along(between(line, part), outward, GAP + reach),
      text,
      width,
      height: LABEL_HEIGHT,
    }));
    const spot = spots.find(
      (label) =>
        ![...lines, ...marks].some((side) => crosses(label, side, 1)) &&
        ![...placed, ...inside].some((other) => overlaps(label, other, apart)),
    );
    if (!spot && strict) return null;
    const chosen = spot ?? spots[0];
    if (chosen) placed.push(chosen);
  }
  return placed;
}

function regularCorners(count: number): ShapePoint[] {
  const radius = 40 + count * 5;
  return Array.from({ length: count }, (_, index) => {
    // The bottom side lies flat. Turning goes clockwise on the screen.
    const turn = Math.PI / 2 - Math.PI / count + (index * 2 * Math.PI) / count;
    return { x: radius * Math.cos(turn), y: radius * Math.sin(turn) };
  });
}

// The size in degrees of the bottom left corner of a leaning shape. A size
// written in a corner is drawn (within reason), so that a corner labelled
// 115° is never the sharp one.
function leanOf(angles: string[], usual: number): number {
  for (const [index, label] of angles.entries()) {
    const degrees = degreesOf(label);
    if (degrees === null) continue;
    const lean = index % 2 === 0 ? degrees : 180 - degrees;
    return Math.min(140, Math.max(40, lean));
  }
  return usual;
}

function leaning(bottom: number, slant: number, lean: number): ShapePoint[] {
  const turn = (lean * Math.PI) / 180;
  const top = { x: slant * Math.cos(turn), y: -slant * Math.sin(turn) };
  return [
    { x: bottom, y: 0 },
    { x: 0, y: 0 },
    top,
    { x: top.x + bottom, y: top.y },
  ];
}

function uprightCorners(width: number, height: number): ShapePoint[] {
  return [
    { x: width, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: -height },
    { x: width, y: -height },
  ];
}

// The lengths written on the sides, as numbers, or null for a side with no
// length ("", "?" or a letter). Lengths in more than one unit are not
// compared, so then none is known.
function lengthsOf(sides: string[]): (number | null)[] {
  const units = new Set<string>();
  const sizes = sides.map((label) => {
    if (!LENGTH.test(label)) return null;
    const length = /^(\d+(?:\.\d+)?) ?(.*)$/.exec(label);
    if (!length) return null;
    units.add(length[2] ?? '');
    return Number(length[1]);
  });
  return units.size > 1 ? sides.map(() => null) : sizes;
}

const clamp = (value: number, low: number, high: number) =>
  Math.min(high, Math.max(low, value));
const RADIANS = Math.PI / 180;

// Scaled to fit inside width by height.
function fitted(
  corners: ShapePoint[],
  width: number,
  height: number,
): ShapePoint[] {
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const scale = Math.min(
    width / (Math.max(...xs) - Math.min(...xs) || 1),
    height / (Math.max(...ys) - Math.min(...ys) || 1),
  );
  return corners.map((corner) => ({
    x: corner.x * scale,
    y: corner.y * scale,
  }));
}

// A rectangle follows its labels when they give two different lengths: a
// rectangle 3 by 20 is drawn long and thin, and stands tall when its upright
// side is the longer. Equal labels keep the usual drawing.
function rectangleCorners(sides: string[]): ShapePoint[] {
  const [b0, u0, b2, u3] = lengthsOf(sides);
  const bottom = b0 ?? b2;
  const upright = u0 ?? u3;
  if (bottom == null || upright == null || bottom === upright || bottom <= 0)
    return uprightCorners(150, 90);
  // A little narrower than the usual drawing, so that with a label on each
  // upright side it is no wider than a square with its labels.
  const ratio = clamp(upright / bottom, 0.3, 1 / 0.3);
  return ratio < 1
    ? uprightCorners(110, Math.max(40, 110 * ratio))
    : uprightCorners(Math.max(40, 130 / ratio), 130);
}

// A parallelogram whose bottom and sloping sides are both labelled is drawn
// with those sides in proportion (within limits): a sloping side labelled
// longer than the bottom side is drawn longer.
function parallelogramCorners(shape: Shape2dSpec): ShapePoint[] {
  const lean = leanOf(shape.angles, 65);
  const [b0, s1, b2, s3] = lengthsOf(shape.sides);
  const bottom = b0 ?? b2;
  const slant = s1 ?? s3;
  if (bottom == null || slant == null || bottom <= 0 || slant <= 0)
    return leaning(135, 85, lean);
  if (bottom === slant) return leaning(110, 110, lean);
  const ratio = clamp(slant / bottom, 0.35, 1 / 0.35);
  return ratio < 1
    ? leaning(135, 135 * ratio, lean)
    : leaning(115 / ratio, 115, lean);
}

// The sizes of the three corners of a triangle, from its corner labels and
// its name, as [bottom left, top, bottom right]; null when they are not all
// known, or do not make a triangle.
function triangleAngles(shape: Shape2dSpec): number[] | null {
  // Angle 0 is at the bottom left, angle 1 at the top, angle 2 at the bottom
  // right.
  const given = shape.angles.map(degreesOf);
  let [left, top, right] = [
    given[0] ?? null,
    given[1] ?? null,
    given[2] ?? null,
  ];
  const rest = (a: number | null, b: number | null) =>
    a !== null && b !== null ? 180 - a - b : null;
  switch (shape.shape) {
    case 'right_triangle': {
      // The right angle is wherever the picture puts it.
      const corner = shape.rightAngleAt ?? 'bottom_left';
      const square = { bottom_left: left, top, bottom_right: right }[corner];
      if (square !== null && Math.abs(square - 90) > 0.5) return null;
      if (corner === 'bottom_left') left = 90;
      if (corner === 'top') top = 90;
      if (corner === 'bottom_right') right = 90;
      break;
    }
    case 'isosceles_triangle':
      if (left !== null && right !== null && Math.abs(left - right) > 0.5)
        return null;
      left = left ?? right;
      if (left === null && top !== null) left = (180 - top) / 2;
      right = left;
      break;
    case 'scalene_triangle':
      break;
    default:
      return null;
  }
  left = left ?? rest(top, right);
  top = top ?? rest(left, right);
  right = right ?? rest(left, top);
  if (left === null || top === null || right === null) return null;
  const sizes = [left, top, right];
  if (Math.abs(left + top + right - 180) > 1) return null;
  return sizes.every((size) => size >= 8) ? sizes : null;
}

// The lengths of the three sides, bottom then clockwise, from the side
// labels and the triangle's name; null when too few are known.
function triangleLengths(shape: Shape2dSpec): number[] | null {
  const [bottom, left, right] = lengthsOf(shape.sides).map((size) =>
    size !== null && size > 0 ? size : null,
  );
  switch (shape.shape) {
    case 'isosceles_triangle': {
      if (left != null && right != null && left !== right) return null;
      const leg = left ?? right;
      return bottom != null && leg != null ? [bottom, leg, leg] : null;
    }
    case 'scalene_triangle': {
      const known = [bottom, left, right].filter(
        (size): size is number => size != null,
      );
      if (known.length < 2) return null;
      // A side to find is drawn halfway between the other two, so that
      // the drawing gives nothing away.
      const middle = ((known[0] ?? 0) + (known[1] ?? 0)) / 2;
      return [bottom ?? middle, left ?? middle, right ?? middle];
    }
    default:
      return null;
  }
}

// The corner sizes a triangle is drawn with. A corner much smaller than 38°
// leaves no room near it for its label, so the sizes are drawn a little
// closer to 60° (each moved the same share of the way): they still add up to
// 180°, the larger corner is still drawn larger, equal corners stay equal,
// and an obtuse corner stays obtuse. A right angle is always drawn square.
function softened(sizes: number[]): number[] {
  const least = Math.min(...sizes);
  const most = Math.max(...sizes);
  if (sizes.some((size) => Math.abs(size - 90) < 0.5)) {
    // A right angle stays square; the other two move toward 45° until the
    // smaller is at least 30°.
    if (least >= 30) return sizes;
    const share = (45 - 30) / (45 - least);
    return sizes.map((size) =>
      Math.abs(size - 90) < 0.5 ? size : 45 + (size - 45) * share,
    );
  }
  if (least >= 38) return sizes;
  let share = (60 - 38) / (60 - least);
  if (most > 90) share = Math.max(share, 32 / (most - 60));
  share = Math.min(1, share);
  return sizes.map((size) => 60 + (size - 60) * share);
}

// The three sizes, drawn from a bottom side of length 1 with its left end
// at (0, 0) and the top above it.
function triangleFromAngles(left: number, top: number): ShapePoint[] {
  const right = 180 - left - top;
  // The side from the bottom left corner up to the top.
  const rise = Math.sin(right * RADIANS) / Math.sin(top * RADIANS);
  return [
    { x: 1, y: 0 },
    { x: 0, y: 0 },
    { x: rise * Math.cos(left * RADIANS), y: -rise * Math.sin(left * RADIANS) },
  ];
}

// The smallest corner of a triangle with these sides, in degrees, or 0 when
// the sides do not make one.
function smallestAngle([a, b, c]: number[]): number {
  if (a === undefined || b === undefined || c === undefined) return 0;
  if (a + b <= c || b + c <= a || a + c <= b) return 0;
  const facing = (side: number, one: number, other: number) =>
    Math.acos((one * one + other * other - side * side) / (2 * one * other)) /
    RADIANS;
  return Math.min(facing(a, b, c), facing(b, a, c), facing(c, a, b));
}

function triangleFromLengths(lengths: number[]): ShapePoint[] | null {
  // Lengths that cannot make a triangle, or make a very thin one, are drawn
  // closer together (each raised to a power below 1) until they make a
  // triangle that can be seen: the longest side is still drawn longest, and
  // equal sides stay equal.
  let drawn = lengths;
  for (let power = 1; power >= 0; power -= 0.05) {
    drawn = lengths.map((size) => size ** power);
    if (smallestAngle(drawn) >= 30) break;
  }
  const [bottom, left, right] = drawn as [number, number, number];
  const across = (bottom * bottom + left * left - right * right) / (2 * bottom);
  const up = Math.sqrt(Math.max(0, left * left - across * across));
  return [
    { x: bottom, y: 0 },
    { x: 0, y: 0 },
    { x: across, y: -up },
  ];
}

// A right triangle with its bottom side flat, clockwise from the right-hand
// end of the bottom side. With the right angle at a bottom corner, `a` is
// the bottom side and `b` the upright one. With it at the top, `a` is the
// side from the bottom left corner up to the top and `b` the side from the
// top down to the bottom right corner, so the long side is at the bottom.
function rightTriangleCorners(
  corner: RightAngleCorner,
  a: number,
  b: number,
): ShapePoint[] {
  if (corner === 'bottom_left')
    return [
      { x: a, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: -b },
    ];
  if (corner === 'bottom_right')
    return [
      { x: a, y: 0 },
      { x: 0, y: 0 },
      { x: a, y: -b },
    ];
  const long = Math.hypot(a, b);
  return [
    { x: long, y: 0 },
    { x: 0, y: 0 },
    { x: (a * a) / long, y: -(a * b) / long },
  ];
}

// A triangle drawn from its labels: its corner sizes when they are known,
// otherwise its side lengths. Null when the labels say too little, and the
// usual drawing is used.
function labelledTriangle(shape: Shape2dSpec): ShapePoint[] | null {
  const sizes = triangleAngles(shape);
  if (sizes) {
    const [left = 60, top = 60] = softened(sizes);
    // Wider room: a corner labelled with a small size needs its label well
    // inside the shape.
    return fitted(triangleFromAngles(left, top), 180, 130);
  }
  // A triangle with its bottom side and its perpendicular height given is
  // drawn that tall (within limits); a labelled left side sets where the
  // top corner is.
  const [base, rise] = lengthsOf([shape.sides[0] ?? '', shape.height ?? '']);
  if (base != null && rise != null && base > 0 && rise > 0) {
    const tall = clamp(rise / base, 0.4, 1.6);
    const [, slope] = lengthsOf([shape.sides[0] ?? '', shape.sides[1] ?? '']);
    let across = shape.shape === 'isosceles_triangle' ? 0.5 : 0.3;
    if (shape.shape === 'scalene_triangle' && slope != null && slope > rise)
      across = clamp(Math.sqrt(slope * slope - rise * rise) / base, -0.5, 1.5);
    return fitted(
      [
        { x: 1, y: 0 },
        { x: 0, y: 0 },
        { x: across, y: -tall },
      ],
      160,
      130,
    );
  }
  if (shape.shape === 'right_triangle') {
    const corner = shape.rightAngleAt ?? 'bottom_left';
    const [s0, s1, s2] = lengthsOf(shape.sides);
    // The two sides that meet at the right angle and the long side, from
    // the sides clockwise from the bottom.
    const [legA, legB, long] =
      corner === 'bottom_left'
        ? [s0, s1, s2]
        : corner === 'bottom_right'
          ? [s0, s2, s1]
          : [s1, s2, s0];
    let a = legA;
    let b = legB;
    if (a == null && b != null && long != null && long > b)
      a = Math.sqrt(long * long - b * b);
    if (b == null && a != null && long != null && long > a)
      b = Math.sqrt(long * long - a * a);
    if (a == null || b == null || a <= 0 || b <= 0) return null;
    const ratio = clamp(b / a, 0.4, 2.5);
    return fitted(rightTriangleCorners(corner, 1, ratio), 145, 130);
  }
  const lengths = triangleLengths(shape);
  const corners = lengths && triangleFromLengths(lengths);
  return corners ? fitted(corners, 160, 130) : null;
}

// A kite drawn from its corner sizes, or from the lengths of its lower and
// upper sides. Null when its labels say too little.
function labelledKite(shape: Shape2dSpec): ShapePoint[] | null {
  // Angle 0 is the left corner, 1 the top, 2 the right, 3 the bottom.
  const [left, top, right, bottom] = shape.angles.map(degreesOf);
  const side = left ?? right ?? null;
  let tip = top ?? null;
  let foot = bottom ?? null;
  if (tip === null && foot !== null && side !== null)
    tip = 360 - foot - 2 * side;
  if (foot === null && tip !== null && side !== null)
    foot = 360 - tip - 2 * side;
  const half = 52;
  if (
    tip !== null &&
    foot !== null &&
    tip >= 15 &&
    tip <= 165 &&
    foot >= 15 &&
    foot <= 165
  ) {
    // A corner much sharper than 50° leaves no room for the labels, so the
    // corners are drawn a little closer to 90° (each moved the same share of
    // the way): they still add up to 360°, a wider corner is still drawn
    // wider, and no corner crosses over 90°.
    const least = Math.min(tip, foot, (360 - tip - foot) / 2);
    if (least < 50) {
      const share = (90 - 50) / (90 - least);
      tip = 90 + (tip - 90) * share;
      foot = 90 + (foot - 90) * share;
    }
    const low = half / Math.tan((foot / 2) * RADIANS);
    const high = half / Math.tan((tip / 2) * RADIANS);
    return fitted(
      [
        { x: 0, y: 0 },
        { x: -half, y: -low },
        { x: 0, y: -low - high },
        { x: half, y: -low },
      ],
      150,
      150,
    );
  }
  const [l0, u1, u2, l3] = lengthsOf(shape.sides);
  const lower = l0 ?? l3;
  const upper = u1 ?? u2;
  if (lower == null || upper == null || lower <= 0 || upper <= 0) return null;
  if (l0 != null && l3 != null && l0 !== l3) return null;
  if (u1 != null && u2 != null && u1 !== u2) return null;
  const ratio = clamp(upper / lower, 0.4, 2.5);
  const width = 0.75 * Math.min(1, ratio);
  const low = Math.sqrt(1 - width * width);
  const high = Math.sqrt(ratio * ratio - width * width);
  return fitted(
    [
      { x: 0, y: 0 },
      { x: -width, y: -low },
      { x: 0, y: -low - high },
      { x: width, y: -low },
    ],
    150,
    150,
  );
}

// A trapezium drawn from its corner sizes and the lengths of its parallel
// sides, where they are given. Null when neither is given.
function labelledTrapezium(shape: Shape2dSpec): ShapePoint[] | null {
  // Angle 0 is the bottom left corner, 1 the top left, 2 the top right and
  // 3 the bottom right.
  const [a0, a1, a2, a3] = shape.angles.map(degreesOf);
  const even = isIsoscelesTrapezium(shape);
  let left = a0 ?? (a1 != null ? 180 - a1 : null);
  let right = a3 ?? (a2 != null ? 180 - a2 : null);
  if (even) {
    left = left ?? right;
    right = right ?? left;
  }
  const [b0, , t2] = lengthsOf(shape.sides);
  const ratio =
    b0 != null && t2 != null && b0 > 0 && t2 > 0
      ? clamp(t2 / b0, 0.3, 1.6)
      : null;
  if (left === null && right === null && ratio === null) return null;
  const bottom = 160;
  let height = 90;
  let offLeft: number;
  let offRight: number;
  const usable = (size: number | null) =>
    size !== null && size >= 15 && size <= 165;
  if (left !== null && right !== null && usable(left) && usable(right)) {
    const lean = 1 / Math.tan(left * RADIANS) + 1 / Math.tan(right * RADIANS);
    if (ratio !== null && lean * (1 - ratio) > 0.01)
      height = clamp((bottom * (1 - ratio)) / lean, 40, 140);
    // The top side is never squeezed shorter than 40.
    if (lean > 0 && bottom - height * lean < 40) height = (bottom - 40) / lean;
    offLeft = height / Math.tan(left * RADIANS);
    offRight = height / Math.tan(right * RADIANS);
  } else {
    const gap = bottom - (ratio !== null ? bottom * ratio : 82);
    if (left !== null && usable(left)) {
      offLeft = height / Math.tan(left * RADIANS);
      offRight = gap - offLeft;
    } else if (right !== null && usable(right)) {
      offRight = height / Math.tan(right * RADIANS);
      offLeft = gap - offRight;
    } else if (ratio !== null) {
      offLeft = even ? gap / 2 : 22;
      offRight = gap - offLeft;
    } else return null;
  }
  const corners = [
    { x: bottom, y: 0 },
    { x: 0, y: 0 },
    { x: offLeft, y: -height },
    { x: bottom - offRight, y: -height },
  ];
  // The top side must still run left to right.
  if ((corners[3]?.x ?? 0) - (corners[2]?.x ?? 0) < 20) return null;
  return fitted(corners, 190, 140);
}

// A shape that is usually regular, drawn from its corner sizes and side
// lengths when they are given and close up into a shape: an octagon with
// sides of 2 cm and 4 cm in turn. Null otherwise.
function labelledPolygon(
  shape: Shape2dSpec,
  count: number,
): ShapePoint[] | null {
  const sizes = shape.angles.map(degreesOf);
  const lengths = lengthsOf(shape.sides);
  const anyLength = lengths.some((size) => size !== null);
  const allLengths = lengths.every((size) => size !== null && size > 0);
  const allSizes = sizes.every((size) => size !== null);
  if (anyLength && !allLengths) return null;
  if (!allLengths && !allSizes) return null;
  const turns = sizes.map((size) =>
    allSizes && size !== null ? size : 180 - 360 / count,
  );
  if (
    Math.abs(turns.reduce((sum, size) => sum + size, 0) - (count - 2) * 180) > 1
  )
    return null;
  const steps = allLengths
    ? lengths.map((size) => size ?? 1)
    : lengths.map(() => 1);
  const corners: ShapePoint[] = [];
  let at = { x: 0, y: 0 };
  let heading = 180;
  for (const [index, step] of steps.entries()) {
    corners.push(at);
    at = {
      x: at.x + step * Math.cos(heading * RADIANS),
      y: at.y + step * Math.sin(heading * RADIANS),
    };
    // Angle i sits where side i ends.
    heading += 180 - (turns[index] ?? 0);
  }
  const perimeter = steps.reduce((sum, step) => sum + step, 0);
  if (Math.hypot(at.x, at.y) > perimeter * 0.01) return null;
  // Equal sides and equal corners: the usual regular drawing.
  if (
    steps.every((step) => step === steps[0]) &&
    turns.every((size) => Math.abs(size - (turns[0] ?? 0)) < 0.01)
  )
    return null;
  const across = 2.3 * (40 + count * 5);
  return fitted(corners, across, across);
}

// The corners of a shape with straight sides, clockwise from the right-hand
// end of the bottom side, so that side i runs from corner i to corner i + 1
// and angle i sits at corner i + 1. Null for a shape with a curved side.
function cornersOf(shape: Shape2dSpec): ShapePoint[] | null {
  const regular = REGULAR[shape.shape];
  if (regular !== undefined)
    return labelledPolygon(shape, regular) ?? regularCorners(regular);
  switch (shape.shape) {
    case 'isosceles_triangle':
    case 'scalene_triangle':
    case 'right_triangle': {
      const drawn = labelledTriangle(shape);
      if (drawn) return drawn;
      break;
    }
    case 'kite': {
      const drawn = labelledKite(shape);
      if (drawn) return drawn;
      break;
    }
    case 'trapezium': {
      const drawn = labelledTrapezium(shape);
      if (drawn) return drawn;
      break;
    }
    default:
      break;
  }
  switch (shape.shape) {
    case 'circle':
    case 'semicircle':
    case 'oval':
    case 'heart':
      return null;
    case 'equilateral_triangle':
      return [
        { x: 60, y: 0 },
        { x: -60, y: 0 },
        { x: 0, y: -103.92 },
      ];
    case 'isosceles_triangle':
      return [
        { x: 42, y: 0 },
        { x: -42, y: 0 },
        { x: 0, y: -120 },
      ];
    case 'scalene_triangle':
      return [
        { x: 150, y: 0 },
        { x: 0, y: 0 },
        { x: 42, y: -90 },
      ];
    case 'right_triangle':
      // The right angle is at the bottom left unless the picture says.
      return shape.rightAngleAt === 'top'
        ? fitted(rightTriangleCorners('top', 3, 4), 170, 130)
        : rightTriangleCorners(shape.rightAngleAt ?? 'bottom_left', 135, 95);
    case 'square':
      return uprightCorners(105, 105);
    case 'rectangle':
      return rectangleCorners(shape.sides);
    case 'rhombus':
      return leaning(105, 105, leanOf(shape.angles, 60));
    case 'parallelogram':
      return parallelogramCorners(shape);
    case 'trapezium':
      // The parallel sides are the top and the bottom.
      return isIsoscelesTrapezium(shape)
        ? [
            { x: 160, y: 0 },
            { x: 0, y: 0 },
            { x: 38, y: -90 },
            { x: 122, y: -90 },
          ]
        : [
            { x: 160, y: 0 },
            { x: 0, y: 0 },
            { x: 22, y: -90 },
            { x: 104, y: -90 },
          ];
    case 'kite':
      // The line of symmetry is upright; the lower sides are the long ones.
      return [
        { x: 0, y: 0 },
        { x: -52, y: -92 },
        { x: 0, y: -132 },
        { x: 52, y: -92 },
      ];
    case 'star_5':
      return Array.from({ length: 10 }, (_, index) => {
        const radius = index % 2 === 0 ? 62 : 23.7;
        const turn = -Math.PI / 2 + (index * Math.PI) / 5;
        return { x: radius * Math.cos(turn), y: radius * Math.sin(turn) };
      });
    case 'plus_sign':
      return [
        { x: 80, y: 0 },
        { x: 40, y: 0 },
        { x: 40, y: -40 },
        { x: 0, y: -40 },
        { x: 0, y: -80 },
        { x: 40, y: -80 },
        { x: 40, y: -120 },
        { x: 80, y: -120 },
        { x: 80, y: -80 },
        { x: 120, y: -80 },
        { x: 120, y: -40 },
        { x: 80, y: -40 },
      ];
    case 'l_shape':
      return [
        { x: 105, y: 0 },
        { x: 0, y: 0 },
        { x: 0, y: -125 },
        { x: 48, y: -125 },
        { x: 48, y: -48 },
        { x: 105, y: -48 },
      ];
    case 'arrow':
      return [
        { x: 0, y: 18 },
        { x: 0, y: -18 },
        { x: 85, y: -18 },
        { x: 85, y: -48 },
        { x: 150, y: 0 },
        { x: 85, y: 48 },
        { x: 85, y: 18 },
      ];
    default:
      return null;
  }
}

// The outline of a shape with a curved side, and the box it fills.
function curveOf(shape: ShapeName): { outline: string; reach: ShapePoint[] } {
  if (shape === 'circle')
    return {
      outline: 'M -58 0 A 58 58 0 1 1 58 0 A 58 58 0 1 1 -58 0 Z',
      reach: [
        { x: -58, y: -58 },
        { x: 58, y: 58 },
      ],
    };
  // An oval: wider than tall, so it is never taken for a circle.
  if (shape === 'oval')
    return {
      outline: 'M -82 0 A 82 50 0 1 1 82 0 A 82 50 0 1 1 -82 0 Z',
      reach: [
        { x: -82, y: -50 },
        { x: 82, y: 50 },
      ],
    };
  if (shape === 'semicircle')
    return {
      outline: 'M -72 0 A 72 72 0 0 1 72 0 Z',
      reach: [
        { x: -72, y: -72 },
        { x: 72, y: 0 },
      ],
    };
  // A heart: the two halves mirror each other across the upright line.
  return {
    outline:
      'M 0 0 C -74 -42 -84 -112 -40 -112 C -16 -112 0 -98 0 -82 C 0 -98 16 -112 40 -112 C 84 -112 74 -42 0 0 Z',
    reach: [
      { x: -70, y: -112 },
      { x: 70, y: 0 },
    ],
  };
}

// The corners that are drawn square, when the picture marks right angles.
function rightCorners(shape: Shape2dSpec, corners: ShapePoint[]): number[] {
  if (!shape.marks.rightAngles) return [];
  const clockwise = isClockwise(corners);
  const count = corners.length;
  const found: number[] = [];
  for (const [index, corner] of corners.entries()) {
    const before = corners[(index + count - 1) % count];
    const after = corners[(index + 1) % count];
    if (!before || !after) continue;
    const a = unit(corner, before);
    const b = unit(corner, after);
    const square = Math.abs(a.x * b.x + a.y * b.y) < 0.01;
    // A corner that points inwards is not a right angle of the shape.
    const outwards = a.x * b.y - a.y * b.x < 0 === clockwise;
    if (square && outwards) found.push(index);
  }
  return found;
}

// A label inside each corner that has one, on the line that halves the
// corner and far enough in to clear both sides and any mark on them.
function placeAngleLabels(
  corners: ShapePoint[],
  labels: string[],
  squared: number[],
  marks: Line[],
): ShapePictureLabel[] {
  const count = corners.length;
  const placed: ShapePictureLabel[] = [];
  for (const [index, text] of labels.entries()) {
    // Angle i sits where side i ends.
    const at = (index + 1) % count;
    const corner = corners[at];
    const before = corners[index % count];
    const after = corners[(at + 1) % count];
    if (text === '' || !corner || !before || !after) continue;
    const a = unit(corner, before);
    const b = unit(corner, after);
    const half = Math.acos(a.x * b.x + a.y * b.y) / 2;
    const width = labelWidth(text);
    const clear = Math.hypot(width, LABEL_HEIGHT) / 2 + 2;
    const inward = unit({ x: 0, y: 0 }, { x: a.x + b.x, y: a.y + b.y });
    const nearest =
      clear / Math.sin(half) + (squared.includes(at) ? SQUARE : 0);
    const spots = Array.from({ length: 20 }, (_, step) => ({
      ...along(corner, inward, nearest + step * 3),
      text,
      width,
      height: LABEL_HEIGHT,
    }));
    // Clear of the marks, and of the label of a corner before it (two
    // narrow corners can send their labels to the same place).
    const chosen =
      spots.find(
        (label) =>
          !marks.some((mark) => crosses(label, mark, 1)) &&
          !placed.some((other) => overlaps(label, other, 2)),
      ) ??
      spots.find((label) => !marks.some((mark) => crosses(label, mark, 1))) ??
      spots[0];
    if (chosen) placed.push(chosen);
  }
  return placed;
}

function squareAt(corners: ShapePoint[], index: number): ShapePoint[] {
  const count = corners.length;
  const corner = corners[index];
  const before = corners[(index + count - 1) % count];
  const after = corners[(index + 1) % count];
  if (!corner || !before || !after) return [];
  const a = unit(corner, before);
  const b = unit(corner, after);
  return [
    along(corner, a, SQUARE),
    along(along(corner, a, SQUARE), b, SQUARE),
    along(corner, b, SQUARE),
  ];
}

// One stroke across the side for the first group of equal sides, two for
// the second. They move off the middle when the side also has an arrow.
function ticksOn(line: Line, strokes: number, part: number): Line[] {
  const way = unit(line.from, line.to);
  const across = { x: way.y, y: -way.x };
  const middle = between(line, part);
  return Array.from({ length: strokes }, (_, index) => {
    const centre = along(middle, way, (index - (strokes - 1) / 2) * 5);
    return {
      from: along(centre, across, TICK),
      to: along(centre, across, -TICK),
    };
  });
}

// One arrow head for the first pair of parallel sides, two for the second.
function arrowsOn(
  line: Line,
  heads: number,
  part: number,
  flip: boolean,
): ShapePoint[][] {
  const forward = unit(line.from, line.to);
  const way = flip ? { x: -forward.x, y: -forward.y } : forward;
  const across = { x: way.y, y: -way.x };
  const middle = between(line, part);
  return Array.from({ length: heads }, (_, index) => {
    const tip = along(middle, way, ARROW / 2 + (index - (heads - 1) / 2) * 7);
    const back = along(tip, way, -ARROW);
    return [
      along(back, across, ARROW * 0.7),
      tip,
      along(back, across, -ARROW * 0.7),
    ];
  });
}

function boxOf(
  picture: Omit<ShapePicture, 'box' | 'outline'>,
  reach: ShapePoint[],
): ShapePicture['box'] {
  const xs = reach.map((point) => point.x);
  const ys = reach.map((point) => point.y);
  for (const label of [
    ...picture.sideLabels,
    ...picture.angleLabels,
    ...picture.heightLabels,
  ]) {
    xs.push(label.x - label.width / 2, label.x + label.width / 2);
    ys.push(label.y - label.height / 2, label.y + label.height / 2);
  }
  for (const tick of [...picture.ticks, ...picture.dashes]) {
    xs.push(tick.from.x, tick.to.x);
    ys.push(tick.from.y, tick.to.y);
  }
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    x,
    y,
    width: Math.max(...xs) + PAD - x,
    height: Math.max(...ys) + PAD - y,
  };
}

function outlineOf(corners: ShapePoint[]): string {
  return `${corners
    .map(
      (corner, index) =>
        `${index === 0 ? 'M' : 'L'} ${fixed(corner.x)} ${fixed(corner.y)}`,
    )
    .join(' ')} Z`;
}

function compositePicture(shape: CompositeShapeSpec): ShapePicture {
  const xs = shape.points.map((point) => point.x);
  const ys = shape.points.map((point) => point.y);
  const fit = Math.min(
    COMPOSITE_WIDTH / (Math.max(...xs) - Math.min(...xs)),
    COMPOSITE_HEIGHT / (Math.max(...ys) - Math.min(...ys)),
  );
  const drawn = (grow: number) =>
    shape.points.map((point) => ({
      x: point.x * fit * grow,
      // y goes up the page in the data and down it on the screen.
      y: -point.y * fit * grow,
    }));
  // Drawn larger, a step at a time, until every label has room.
  let corners = drawn(GROWTH);
  let sideLabels =
    placeSideLabels(corners, shape.sides, [], [], 2, false) ?? [];
  for (const grow of [1, 1.25, 1.5, 2]) {
    const roomy = placeSideLabels(
      drawn(grow),
      shape.sides,
      [],
      [],
      APART,
      true,
    );
    if (!roomy) continue;
    corners = drawn(grow);
    sideLabels = roomy;
    break;
  }
  const picture = {
    corners,
    sideLabels,
    angleLabels: [],
    ticks: [],
    arrows: [],
    squares: [],
    dashes: [],
    heightLabels: [],
  };
  return {
    ...picture,
    outline: outlineOf(corners),
    box: boxOf(picture, corners),
  };
}

export function shapePicture(
  shape: Shape2dSpec | CompositeShapeSpec,
): ShapePicture {
  if (shape.type === 'composite_shape') return compositePicture(shape);
  let corners = cornersOf(shape);
  if (corners) {
    // A drawing that follows its labels is never wider than the usual
    // drawing of its shape with the same labels (or than WIDEST): a wider
    // picture is shown smaller on a phone, and its labels with it.
    const usual = cornersOf({
      ...shape,
      sides: shape.sides.map(() => ''),
      angles: shape.angles.map(() => ''),
    });
    const most = Math.max(
      WIDEST,
      usual ? drawShape(shape, usual).box.width : 0,
    );
    for (let step = 0; step < 8; step += 1) {
      const width: number = drawShape(shape, corners).box.width;
      if (width <= most + 0.5) break;
      const shrink: number = most / width;
      corners = corners.map((corner): ShapePoint => ({
        x: corner.x * shrink,
        y: corner.y * shrink,
      }));
    }
    return drawShape(shape, corners);
  }
  {
    const curve = curveOf(shape.shape);
    const picture = {
      corners: [],
      sideLabels: [],
      angleLabels: [],
      ticks: [],
      arrows: [],
      squares: [],
      dashes: [],
      heightLabels: [],
    };
    return {
      ...picture,
      outline: curve.outline,
      box: boxOf(picture, curve.reach),
    };
  }
}

// The perpendicular height: a dashed line from a top corner straight down to
// the bottom side, a small square where it meets it, and its label beside
// it. A top corner above the bottom side is used when there is one;
// otherwise the bottom side is carried on, dashed, to meet the height.
function heightOf(
  shape: Shape2dSpec,
  corners: ShapePoint[],
  strokes: Line[],
  taken: ShapePictureLabel[],
): { dashes: Line[]; squares: ShapePoint[][]; labels: ShapePictureLabel[] } {
  const none = { dashes: [], squares: [], labels: [] };
  const [right, left] = corners;
  if (!shape.height || !right || !left) return none;
  const ground = left.y;
  const tops = (shape.shape.endsWith('triangle') ? [2] : [2, 3])
    .map((index) => corners[index])
    .filter((corner): corner is ShapePoint => corner !== undefined);
  const top =
    tops.find((corner) => corner.x > left.x + 4 && corner.x < right.x - 4) ??
    tops[0];
  if (!top) return none;
  const foot = { x: top.x, y: ground };
  const dashes: Line[] = [{ from: top, to: foot }];
  if (foot.x < left.x) dashes.push({ from: left, to: foot });
  if (foot.x > right.x) dashes.push({ from: right, to: foot });
  // The square sits on the side of the height that has the bottom side.
  const toward = (left.x + right.x) / 2 >= foot.x ? 1 : -1;
  const squares = [
    [
      { x: foot.x + toward * SQUARE, y: ground },
      { x: foot.x + toward * SQUARE, y: ground - SQUARE },
      { x: foot.x, y: ground - SQUARE },
    ],
  ];
  const text = shape.height;
  const width = labelWidth(text);
  const middle = (top.y + ground) / 2;
  // Beside the dashed line, inside the shape first, then further out.
  const spots = [0, 12, 24, 36, 48].flatMap((out) =>
    [toward, -toward].flatMap((side) =>
      [0, -10, 10, -20].map((shift) => ({
        x: foot.x + side * (width / 2 + 5 + out),
        y: Math.min(middle + shift, ground - SQUARE - LABEL_HEIGHT / 2 - 2),
        text,
        width,
        height: LABEL_HEIGHT,
      })),
    ),
  );
  const avoid = [...strokes, ...dashes, ...squares.flatMap(sidesOf)];
  const label =
    spots.find(
      (spot) =>
        !avoid.some((line) => crosses(spot, line, 1)) &&
        !taken.some((other) => overlaps(spot, other, 2)),
    ) ?? spots[0];
  return { dashes, squares, labels: label ? [label] : [] };
}

// The picture of a shape with straight sides, drawn on these corners.
function drawShape(shape: Shape2dSpec, corners: ShapePoint[]): ShapePicture {
  const lines = sidesOf(corners);
  const parallel = parallelGroups(shape);
  const marked = new Set(parallel.flat());
  const ticks = equalGroups(shape).flatMap((group, strokes) =>
    group.flatMap((side) => {
      const line = lines[side];
      return line
        ? ticksOn(line, strokes + 1, marked.has(side) ? 0.38 : 0.5)
        : [];
    }),
  );
  const equal = new Set(equalGroups(shape).flat());
  const arrows = parallel.flatMap((group, heads) =>
    group.flatMap((side, index) => {
      const line = lines[side];
      // Opposite sides run opposite ways round the shape; their arrows
      // point the same way.
      return line
        ? arrowsOn(line, heads + 1, equal.has(side) ? 0.64 : 0.5, index === 1)
        : [];
    }),
  );
  const squared = rightCorners(shape, corners);
  const marks = [
    ...ticks,
    ...arrows.flatMap((arrow) => sidesOf(arrow).slice(0, -1)),
  ];
  const angleLabels = placeAngleLabels(corners, shape.angles, squared, marks);
  const height = heightOf(shape, corners, [...lines, ...marks], angleLabels);
  const picture = {
    corners,
    sideLabels:
      placeSideLabels(
        corners,
        shape.sides,
        [...angleLabels, ...height.labels],
        [...marks, ...height.dashes],
        2,
        false,
      ) ?? [],
    angleLabels,
    ticks,
    arrows,
    squares: [
      ...squared.map((index) => squareAt(corners, index)),
      ...height.squares,
    ],
    dashes: height.dashes,
    heightLabels: height.labels,
  };
  return {
    ...picture,
    outline: outlineOf(corners),
    box: boxOf(picture, corners),
  };
}
